import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getAllDepartments } from '@/modules/Employee/services'

import {
    getFilterItems,
    getFilterToggles,
    getWorkdayActions,
    getWorkdayListColumns,
} from '../components/Index/List/workday-list-config'
import { getAllWorkdays } from '../services'
import { WorkDay } from '../types/response'

interface WorkdayFilters {
    year: number
    month: number
    department_ids: number[]
    employee_status: 'active' | 'inactive' | 'all'
}

export const useWorkdayList = () => {
    const { setModal } = useModal()
    const { t } = useTranslation()

    // Current year and month defaults
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1

    // URL params & form
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<WorkdayFilters>({
        defaults: { sort_by: 'work_day_id', sort_order: 'desc' },
        filterDefaults: {
            year: currentYear,
            month: currentMonth,
            employee_status: 'active',
        },
    })

    const { control } = useForm<WorkdayFilters>({
        defaultValues: filters,
    })

    // Departments for filter
    const { data: departmentsData } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.DEPARTMENTS],
            queryFn: getAllDepartments,
        },
        { showLoading: false, showError: false }
    )

    const departmentOptions =
        departmentsData?.results?.map((d) => ({
            label: d.department_name,
            value: d.department_id,
        })) || []

    // Data fetching
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WORKDAYS, queryParams],
        queryFn: () => getAllWorkdays(queryParams),
        placeholderData: true,
    })

    // Edit handler
    const handleEdit = useCallback(
        (row: WorkDay) => {
            setModal('WorkDayModals/Update/index', {
                open: true,
                selectedWorkDayId: row.work_day_id,
                workNDay: row.work_n_day,
                workDayNote: row.work_day_note,
                onClose: () => {
                    setModal('WorkDayModals/Update/index', {
                        open: false,
                        selectedWorkDayId: null,
                        workDayNote: null,
                        workNDay: null,
                        onClose: () => {},
                        refetchData: () => {},
                    })
                },
                refetchData: () => refetch(),
            })
        },
        [setModal, refetch]
    )

    // Memoized configs
    const columns = useMemo(() => getWorkdayListColumns(t), [t])

    const actions = useMemo(
        () => getWorkdayActions({ t, onEdit: handleEdit }),
        [t, handleEdit]
    )

    const filterItems = useMemo(
        () => getFilterItems(t, departmentOptions),
        [t, departmentOptions]
    )

    const filterToggles = useMemo(() => getFilterToggles(t), [t])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        workdays: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        // Form & params
        control,
        setFilter,
        params,
        queryParams,
        searchInputProps,

        // Configs
        columns,
        actions,
        filterItems,
        filterToggles,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        refetch,
        t,
    }
}
